import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckSquare, Square, Loader2, ArrowRight, RotateCcw, ClipboardCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

export const ComplianceAudit = ({ onBeginIntake }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [emailForm, setEmailForm] = useState({ name: "", email: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const sendEmail = async (e) => {
    e.preventDefault();
    setEmailErr("");
    setEmailBusy(true);
    try {
      await api.post(`/assessments/${result.reference_id}/email`, emailForm);
      setEmailSent(true);
    } catch (err) {
      setEmailErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setEmailBusy(false);
    }
  };

  useEffect(() => {
    api.get("/assessments/items").then(({ data }) => setItems(data.items)).catch(() => {});
  }, []);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const generate = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/assessments", { selections: selected });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setSelected([]);
    setError("");
    setEmailForm({ name: "", email: "" });
    setEmailErr("");
    setEmailSent(false);
  };

  return (
    <section id="self-assessment" className="py-20 md:py-32 bg-[#0B1B2B] relative overflow-hidden" data-testid="self-assessment-section">
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)", backgroundSize: "56px 56px" }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Compliance Self-Assessment</p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl text-white tracking-tight leading-tight">
            Diagnose Your Operational Bottlenecks in Five Clicks.
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed text-sm md:text-base">
            Select the pressures your institution is facing. We generate a custom clinical pod solution and route you
            directly into a pre-filled intake for executive review.
          </p>
        </div>

        {!result ? (
          <>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {items.map((item, idx) => {
                const active = selected.includes(item.id);
                return (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    onClick={() => toggle(item.id)}
                    className={`text-left p-5 rounded-xl border transition-all duration-300 ${
                      active
                        ? "bg-[#D4AF37]/10 border-[#D4AF37] shadow-[0_0_24px_rgba(212,175,55,0.15)]"
                        : "bg-white/5 border-white/15 hover:border-[#D4AF37]/50"
                    }`}
                    data-testid={`assessment-item-${item.id}`}
                    aria-pressed={active}
                  >
                    {active ? (
                      <CheckSquare size={18} className="text-[#D4AF37]" />
                    ) : (
                      <Square size={18} className="text-slate-500" />
                    )}
                    <div className={`mt-3 text-sm font-semibold leading-snug ${active ? "text-[#D4AF37]" : "text-white"}`}>
                      {item.label}
                    </div>
                    <div className="mt-1.5 text-xs text-slate-400 leading-relaxed">{item.detail}</div>
                  </motion.button>
                );
              })}
            </div>
            {error && <p className="mt-6 text-sm text-red-400" data-testid="assessment-error">{error}</p>}
            <div className="mt-10 flex items-center flex-wrap gap-5">
              <button
                onClick={generate}
                disabled={selected.length === 0 || busy}
                className="bg-[#D4AF37] text-[#0B1B2B] font-semibold px-8 py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="assessment-generate-button"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Generate My Pod Solution
              </button>
              <span className="text-xs text-slate-400 tracking-wide uppercase" data-testid="assessment-count">
                {selected.length} bottleneck{selected.length === 1 ? "" : "s"} selected
              </span>
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-12 bg-white rounded-xl border border-[#D4AF37]/40 p-8 md:p-10 max-w-4xl"
            data-testid="assessment-result"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
                <ClipboardCheck size={22} className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#64748B]">
                  Diagnostic Reference · <span className="font-semibold text-[#0B1B2B]" data-testid="assessment-reference-id">{result.reference_id}</span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-[#0B1B2B] tracking-tight mt-0.5" data-testid="assessment-headline">
                  {result.headline}
                </h3>
              </div>
            </div>
            <ul className="mt-7 space-y-3">
              {result.solution_points.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#334155] leading-relaxed" data-testid={`assessment-solution-${i}`}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex items-center flex-wrap gap-4">
              <button
                onClick={() => onBeginIntake(result.track, null, { notes: result.prefill_note })}
                className="group bg-[#0B1B2B] text-white font-semibold px-7 py-3.5 rounded-xl hover:bg-[#152a40] transition-colors flex items-center gap-2"
                data-testid="assessment-begin-intake-button"
              >
                Begin Pre-Filled Intake
                <ArrowRight size={16} className="text-[#D4AF37] transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={reset}
                className="text-[#64748B] text-sm font-medium flex items-center gap-2 hover:text-[#0B1B2B] transition-colors"
                data-testid="assessment-reset-button"
              >
                <RotateCcw size={14} /> Start Over
              </button>
            </div>
            <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
              {emailSent ? (
                <p className="text-sm text-emerald-700 font-medium" data-testid="assessment-email-success">
                  Your custom pod solution has been dispatched to your inbox as a branded PDF.
                </p>
              ) : (
                <form onSubmit={sendEmail} className="flex flex-wrap items-center gap-3" data-testid="assessment-email-form">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
                    Email me this solution as a branded PDF:
                  </span>
                  <input
                    className="px-3 py-2.5 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
                    placeholder="Full Name" required value={emailForm.name}
                    onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                    data-testid="assessment-email-name"
                  />
                  <input
                    className="px-3 py-2.5 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
                    type="email" placeholder="Work Email" required pattern="[^@\s]+@[^@\s]+\.[^@\s]{2,}" value={emailForm.email}
                    onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                    data-testid="assessment-email-input"
                  />
                  <button type="submit" disabled={emailBusy} className="bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#e8c968] transition-colors disabled:opacity-60 flex items-center gap-2" data-testid="assessment-email-submit">
                    {emailBusy && <Loader2 size={14} className="animate-spin" />}
                    Send PDF
                  </button>
                  {emailErr && <span className="text-sm text-red-600" data-testid="assessment-email-error">{emailErr}</span>}
                </form>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
